import React, { useState, useEffect } from 'react';
import { primaryButtonSx, pagePaperSx, pageTabsSx, sectionLabelSx, sectionTitleSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Stack,
} from '@mui/material';
import axios from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { API_BASE_URL } from '../../config/api';

interface JobTag {
  job_tag_id: string;
  tag_name: string;
}

interface Team {
  team_id: string;
  team_name: string;
}

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  username: string;
}

interface SentNotification {
  notification_id: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  created_at: string;
  sender_name: string;
  total_recipients: number;
  read_count: number;
}

interface ReadStatus {
  user_id: string;
  first_name: string;
  last_name: string;
  read_at: string | null;
}

const PushNotifications: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedJobTags, setSelectedJobTags] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [priority, setPriority] = useState('normal');
  const [type, setType] = useState('push');
  const [sentNotifications, setSentNotifications] = useState<SentNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterSentBy, setFilterSentBy] = useState<string>('all');
  const [filteredNotifications, setFilteredNotifications] = useState<SentNotification[]>([]);
  const [uniqueSenders, setUniqueSenders] = useState<string[]>([]);
  const [readStatusDialogOpen, setReadStatusDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<SentNotification | null>(null);
  const [readStatus, setReadStatus] = useState<ReadStatus[]>([]);
  const [loadingReadStatus, setLoadingReadStatus] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const [jobTagsResponse, teamsResponse, usersResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/job-tags`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/teams`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setJobTags(jobTagsResponse.data);
        setTeams(teamsResponse.data);
        setUsers(usersResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      }
    };

    fetchData();
    fetchSentNotifications();
  }, []);

  const fetchSentNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      if (!tokenPayload.businessId) return;

      const response = await axios.get(`${API_BASE_URL}/notifications/business/${tokenPayload.businessId}/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSentNotifications(response.data.notifications);
    } catch (error) {
      console.error('Error fetching sent notifications:', error);
      setError('Failed to load notification history');
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    // Extract unique senders from notifications
    const senders = Array.from(new Set(sentNotifications.map(n => n.sender_name))).sort();
    setUniqueSenders(senders);
  }, [sentNotifications]);

  useEffect(() => {
    // Filter notifications when filters change
    let filtered = [...sentNotifications];
    
    if (filterStartDate) {
      filtered = filtered.filter(n => new Date(n.created_at) >= filterStartDate);
    }
    
    if (filterEndDate) {
      filtered = filtered.filter(n => new Date(n.created_at) <= filterEndDate);
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(n => n.type === filterType);
    }
    
    if (filterPriority !== 'all') {
      filtered = filtered.filter(n => n.priority === filterPriority);
    }

    if (filterSentBy !== 'all') {
      filtered = filtered.filter(n => n.sender_name === filterSentBy);
    }
    
    setFilteredNotifications(filtered);
  }, [sentNotifications, filterStartDate, filterEndDate, filterType, filterPriority, filterSentBy]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      // Decode the JWT token to get the business ID
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      if (!tokenPayload.businessId) {
        setError('Business ID not found in token');
        return;
      }

      const payload = {
        title,
        message,
        job_tag_ids: selectedJobTags,
        team_ids: selectedTeams,
        user_ids: selectedUsers,
        type,
        priority,
        business_id: tokenPayload.businessId
      };

      await axios.post(`${API_BASE_URL}/notifications/send-to-groups`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Notification sent successfully!');
      setTitle('');
      setMessage('');
      setSelectedJobTags([]);
      setSelectedTeams([]);
      setSelectedUsers([]);
      setPriority('normal');
      setType('push');
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('Failed to send notification. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewReadStatus = async (notification: SentNotification) => {
    try {
      setLoadingReadStatus(true);
      setSelectedNotification(notification);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/notifications/${notification.notification_id}/read-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReadStatus(response.data.read_status);
      setReadStatusDialogOpen(true);
    } catch (error) {
      console.error('Error fetching read status:', error);
      setError('Failed to load read status');
    } finally {
      setLoadingReadStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: enGB });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader title="Push Notifications" subtitle="Admin" />

      <Paper sx={{ ...pagePaperSx, mb: 2.5 }}>
        <Typography sx={sectionLabelSx}>Send</Typography>
        <Typography sx={{ ...sectionTitleSx, mb: 2 }}>New notification</Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={4}
                required
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  label="Type"
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="alert">Alert</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="message">Message</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="reminder">Reminder</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label="Priority"
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={pageTabsSx}
              >
                <Tab label="Groups" />
                <Tab label="Users" />
              </Tabs>
            </Grid>

            {tabValue === 0 && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tags</InputLabel>
                    <Select
                      multiple
                      value={selectedJobTags}
                      onChange={(e) => setSelectedJobTags(e.target.value as string[])}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={jobTags.find(tag => tag.job_tag_id === value)?.tag_name}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                      size={isMobile ? "small" : "medium"}
                    >
                      {jobTags.map((tag) => (
                        <MenuItem key={tag.job_tag_id} value={tag.job_tag_id}>
                          {tag.tag_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Teams</InputLabel>
                    <Select
                      multiple
                      value={selectedTeams}
                      onChange={(e) => setSelectedTeams(e.target.value as string[])}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={teams.find(team => team.team_id === value)?.team_name}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                      size={isMobile ? "small" : "medium"}
                    >
                      {teams.map((team) => (
                        <MenuItem key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {tabValue === 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="users-select-label">Users</InputLabel>
                  <Select
                    labelId="users-select-label"
                    multiple
                    value={selectedUsers}
                    onChange={(e) => setSelectedUsers(e.target.value as string[])}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const user = users.find(u => u.userId === value);
                          if (!user) return null;
                          return (
                            <Chip
                              key={`chip-${value}`}
                              label={`${user.firstName} ${user.lastName}`}
                              onDelete={() => {
                                setSelectedUsers(prev => prev.filter(id => id !== value));
                              }}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 250,
                        },
                      },
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {users.filter(user => user.userId).map((user) => (
                      <MenuItem 
                        key={`user-${user.userId}`}
                        value={user.userId}
                      >
                        {`${user.firstName} ${user.lastName} (${user.username})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth={isMobile}
                sx={{ ...primaryButtonSx, mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Notification'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={pagePaperSx}>
        <Typography sx={sectionLabelSx}>History</Typography>
        <Typography sx={{ ...sectionTitleSx, mb: 2 }}>Sent notifications</Typography>

        {/* Filter Controls */}
        <Stack 
          direction={isMobile ? "column" : "row"} 
          spacing={2} 
          sx={{ mb: 3 }}
          divider={isMobile ? <Divider /> : null}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={filterStartDate}
              onChange={(newValue) => setFilterStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: isMobile,
                }
              }}
            />
            <DatePicker
              label="End Date"
              value={filterEndDate}
              onChange={(newValue) => setFilterEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: isMobile,
                }
              }}
            />
          </LocalizationProvider>
          <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }} size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Type"
              fullWidth={isMobile}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="alert">Alert</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="message">Message</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="reminder">Reminder</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }} size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              label="Priority"
              fullWidth={isMobile}
            >
              <MenuItem value="all">All Priorities</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: isMobile ? '100%' : 120 }} size="small">
            <InputLabel>Sent By</InputLabel>
            <Select
              value={filterSentBy}
              onChange={(e) => setFilterSentBy(e.target.value)}
              label="Sent By"
              fullWidth={isMobile}
            >
              <MenuItem value="all">All Senders</MenuItem>
              {uniqueSenders.map((sender) => (
                <MenuItem key={sender} value={sender}>
                  {sender}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={() => {
              setFilterStartDate(null);
              setFilterEndDate(null);
              setFilterType('all');
              setFilterPriority('all');
              setFilterSentBy('all');
            }}
            fullWidth={isMobile}
            sx={primaryButtonSx}
          >
            Clear Filters
          </Button>
        </Stack>

        {notificationsLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Alert severity="info">No notifications found matching the selected criteria</Alert>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredNotifications.map((notification) => (
              <Card key={notification.notification_id}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {notification.message}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={notification.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={notification.priority}
                      size="small"
                      color={
                        notification.priority === 'urgent' ? 'error' :
                        notification.priority === 'high' ? 'warning' :
                        'default'
                      }
                    />
                    <Chip
                      label={`${notification.read_count}/${notification.total_recipients} read`}
                      size="small"
                      color={notification.read_count === notification.total_recipients ? 'success' : 'default'}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Sent by: {notification.sender_name} | {formatDate(notification.created_at)}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => handleViewReadStatus(notification)}
                    fullWidth
                  >
                    View Read Status
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <List>
            {filteredNotifications.map((notification) => (
              <React.Fragment key={notification.notification_id}>
                <ListItem>
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {notification.message}
                        </Typography>
                        <br />
                        <Typography component="span" variant="caption">
                          Sent by: {notification.sender_name} | 
                          Type: {notification.type} | 
                          Priority: {notification.priority} | 
                          Recipients: {notification.read_count}/{notification.total_recipients} read | 
                          {formatDate(notification.created_at)}
                        </Typography>
                      </>
                    }
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewReadStatus(notification)}
                  >
                    View Read Status
                  </Button>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Dialog
        open={readStatusDialogOpen}
        onClose={() => setReadStatusDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Read Status - {selectedNotification?.title}
        </DialogTitle>
        <DialogContent>
          {loadingReadStatus ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {readStatus.map((status) => (
                <Card key={status.user_id}>
                  <CardContent>
                    <Typography variant="subtitle1">
                      {status.first_name} {status.last_name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Chip
                        label={status.read_at ? 'Read' : 'Unread'}
                        color={status.read_at ? 'success' : 'warning'}
                        size="small"
                      />
                      {status.read_at && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(status.read_at)}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Read At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {readStatus.map((status) => (
                    <TableRow key={status.user_id}>
                      <TableCell>
                        {status.first_name} {status.last_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.read_at ? 'Read' : 'Unread'}
                          color={status.read_at ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {status.read_at ? formatDate(status.read_at) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => setReadStatusDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotifications; 